import * as E from 'fp-ts/lib/Either';
import {not, Predicate} from 'fp-ts/lib/function';
import * as IO from 'fp-ts/lib/IO';
import * as O from 'fp-ts/lib/Option';
import {pipe} from 'fp-ts/lib/pipeable';
import * as TE from 'fp-ts/lib/TaskEither';
import {Observer} from 'rxjs';

import {TaskSourcesEnum} from '../../../libs/shared/helpers/src/instrumentation/eventLoopPerformanceHelpers';
import {isOnline} from '../../../srcCommon/helpers/networkReachabilityHelpers';
import {reportToBugsnag} from '../../../srcCommon/services/bugsnag/bugsnag';
import {OfflineError} from '../../../srcCommon/services/offline/offlineError';
import {
  inIdleOrHiddenCallbackAsync,
  reduceInIdleOrHiddenCallbackAsync,
} from '../../helpers/async/inIdleCallback';
import {delayAsync, delayWithJitterAsync} from '../../helpers/asyncHelpers';
import {hasPersistedStorageAsync} from '../../helpers/storageHelpers';
import {AppState} from '../../store/appState';
import {Dispatch, StateGetter} from '../../store/appStore';
import {getHasNewPrefetchV3} from '../../store/featureFlags/featureFlagsSelectors';
import {PrefetcherError} from './prefetcherError';
import {Prefetcher} from './prefetchers/base';
import {createReentrantLock, ReentrantLock} from './reentrantLock';

/*
 * Types.
 */

export interface PrefetcherLoopOptions {
  disabledCooldownDelay: number;
  offlineCooldownDelay: number;
  offlineCooldownJitter: number;
  idleCooldownDelay: number;
  errorCooldownDelay: number;
  errorCooldownJitter: number;
  ticksObserver?: Observer<void>;
}

/*
 * API.
 */

export function startPrefetcherLoop(
  dispatch: Dispatch,
  getState: StateGetter,
  prefetchers: ReadonlyArray<Prefetcher<any>>,
  options: PrefetcherLoopOptions,
) {
  const {errorCooldownDelay, errorCooldownJitter} = options;

  let isTicking = false;
  const lock = createReentrantLock();

  const tick = async () => {
    if (!isTicking) {
      return;
    }

    // Check for dangling promises, out of an abundance of caution.
    lock.ensureIsUnlocked();

    try {
      await tickAsync(dispatch, getState, prefetchers, options, lock);
    } catch (error) {
      reportToBugsnag(error);

      // Pause before trying again, to be safe.
      await delayWithJitterAsync(errorCooldownDelay, errorCooldownJitter);
    } finally {
      tick();
    }
  };

  isTicking = true;
  tick();

  return () => {
    isTicking = false;
  };
}

/*
 * Helpers.
 */

async function tickAsync(
  dispatch: Dispatch,
  getState: StateGetter,
  prefetchers: ReadonlyArray<Prefetcher<any>>,
  options: PrefetcherLoopOptions,
  lock: ReentrantLock,
) {
  const {disabledCooldownDelay, offlineCooldownDelay, offlineCooldownJitter, ticksObserver} = options;

  // If we're definitely offline, pause before trying again.
  if (!isOnline()) {
    await delayWithJitterAsync(offlineCooldownDelay, offlineCooldownJitter);
    return;
  }

  const state = getState();

  // The prefetcher is currently disabled because we don't support offline restart.
  const isPrefetcherEnabled = false && getHasNewPrefetchV3(state) && (await hasPersistedStorageAsync());
  if (!isPrefetcherEnabled) {
    await delayAsync(disabledCooldownDelay);
    return;
  }

  // Wait for the idle callback and the prefetcher inside to finish.
  try {
    await prefetchAsync(dispatch, getState, prefetchers, options, lock);
  } catch (error) {
    // If we have a network error, better luck next time.
    if (error instanceof OfflineError) {
      await delayWithJitterAsync(offlineCooldownDelay, offlineCooldownJitter);
      return;
    }

    // Otherwise, make the loop deal with it.
    throw error;
  } finally {
    if (ticksObserver) {
      ticksObserver.next();
    }
  }
}

async function prefetchAsync(
  dispatch: Dispatch,
  getState: StateGetter,
  prefetchers: ReadonlyArray<Prefetcher<any>>,
  options: PrefetcherLoopOptions,
  lock: ReentrantLock,
) {
  const task = await findPrefetchTaskAsync(dispatch, getState, prefetchers);

  // If the deadline expired or no prefetcher wants to do anything, pause before trying again.
  if (!task) {
    await delayAsync(options.idleCooldownDelay);
    return;
  }

  // Acquire the re-entrant lock to guard against dangling promises, out of an abundance of caution.
  await inIdleOrHiddenCallbackAsync(() => lock.lock(runTaskEitherAsync(task)), {
    source: TaskSourcesEnum.PREFETCH,
  });
}

function findPrefetchTaskAsync(
  dispatch: Dispatch,
  getState: StateGetter,
  prefetchers: ReadonlyArray<Prefetcher<any>>,
) {
  return reduceInIdleOrHiddenCallbackAsync(
    prefetchers,
    (task: TE.TaskEither<PrefetcherError | OfflineError, void> | undefined, prefetcher) =>
      task || maybePrefetchWithState(dispatch, getState, prefetcher, getState()),
    undefined,
    {
      source: TaskSourcesEnum.PREFETCH,
    },
  );
}

const maybePrefetchWithState = <C>(
  dispatch: Dispatch,
  getState: StateGetter,
  prefetcher: Prefetcher<C>,
  startState: AppState,
): TE.TaskEither<PrefetcherError | OfflineError, void> | undefined =>
  pipe(
    startState,
    prefetcher.computeCondition,
    O.map(prefetchWithCondition(dispatch, getState, prefetcher)),
    O.toUndefined,
  );

const prefetchWithCondition =
  <C>(dispatch: Dispatch, getState: StateGetter, prefetcher: Prefetcher<C>) =>
  (startCondition: C): TE.TaskEither<PrefetcherError | OfflineError, void> =>
    pipe(
      TE.tryCatch(() => prefetcher.prefetchAsync(dispatch, startCondition), E.toError),
      // Chain the condition check, but keep the first right-hand value (task result).
      TE.chainFirst(() =>
        TE.fromIOEitherK(ensurePrefetchConditionChanged)(getState, prefetcher, startCondition),
      ),
      // Wrap left-hand value (task error) in a `PrefetcherError` for instrumentation.
      TE.mapLeft((error) =>
        error instanceof OfflineError ? error : new PrefetcherError(prefetcher.key, error),
      ),
    );

const ensurePrefetchConditionChanged = <C>(
  getState: IO.IO<AppState>,
  prefetcher: Prefetcher<C>,
  startCondition: C,
): IO.IO<E.Either<Error, O.Option<C>>> =>
  pipe(getState, IO.map(ensurePrefetchConditionChangedWithState(prefetcher, startCondition)));

const ensurePrefetchConditionChangedWithState =
  <C>(prefetcher: Prefetcher<C>, startCondition: C) =>
  (endState: AppState): E.Either<Error, O.Option<C>> =>
    pipe(
      endState,
      prefetcher.computeCondition,
      E.fromPredicate(
        prefetchConditionChanged(prefetcher, startCondition),
        () => new Error('Prefetcher is not converging'),
      ),
    );

/** Creates a predicate requiring the prefetch condition to have changed after prefetching. */
const prefetchConditionChanged = <C>(
  prefetcher: Prefetcher<C>,
  startCondition: C,
): Predicate<O.Option<C>> => {
  // Get the functional type class for Option<C>.
  const OC = O.getEq(prefetcher.C);

  return not((endConditionOption) => OC.equals(O.some(startCondition), endConditionOption));
};

const runTaskEitherAsync: <A>(task: TE.TaskEither<any, A>) => Promise<A> = (task) =>
  task().then(
    E.fold(
      (error) => Promise.reject(error),
      (value) => Promise.resolve(value),
    ),
  );
